import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PageContainer from '@components/Layout/PageContainer';
import PageHeader from '@features/PageHeader';
import { ModalConfirm } from '@components/Modal';
import Button from '@components/Button';
import AiButton from '@components/AiButton';
import { useTranslation } from '@hooks';
import usePermissions from '@hooks/usePermissions';
import { alertActions, menusActions } from '@actions';
import { handleApiError } from '@helpers/api';
import { clampPageAfterRemoval } from '@helpers/pagination';
import { menusService } from '@services';
import { VIEW_TYPE } from '@constants/microsites';
import { DEFAULT_CURRENCY_BY_LOCALE } from '@constants/currencies';
import { DEFAULT_PAGE_SIZE } from '@constants/pagination';
import { MenusIcon } from 'assets/images/icons';
import EditModal from './EditModal';
import ImportModal from './ImportModal';
import MenusTable from './MenusTable';

const MenusPage = () => {
    const { t } = useTranslation();

    const [selectedMenu, setSelectedMenu] = useState(null);
    const [modal, setModal] = useState({ open: false, type: VIEW_TYPE.CREATE });
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [menuToDelete, setMenuToDelete] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);

    const account = useSelector(state => state.account?.account);
    const totalCount = useSelector(state => state.menus?.data?.totalCount || 0);
    const { hasFullAccess } = usePermissions();

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(menusActions.getAll());
    }, [dispatch]);

    const canManage = hasFullAccess('microsites');

    const onClickMenu = (menu, type) => {
        switch (type) {
            case 'edit':
                setSelectedMenu(menu);
                setModal({ open: true, type: VIEW_TYPE.EDIT });
                break;
            case 'view':
                setSelectedMenu(menu);
                setModal({ open: true, type: VIEW_TYPE.VIEW });
                break;
            case 'duplicate':
                onDuplicate(menu);
                break;
            case 'delete':
                setMenuToDelete(menu);
                break;
            default:
                break;
        }
    };

    const onDuplicate = async menu => {
        try {
            await menusService.duplicateMenu(menu.id);
            dispatch(menusActions.getAll(page, rowsPerPage));
            dispatch(alertActions.success(t('alertMessages.createSuccess')));
        } catch (error) {
            handleApiError(error);
        }
    };

    const onDelete = async () => {
        try {
            await menusService.deleteMenu(menuToDelete.id);
            setMenuToDelete(null);
            const targetPage = clampPageAfterRemoval(page, totalCount, rowsPerPage);
            setPage(targetPage);
            dispatch(menusActions.getAll(targetPage, rowsPerPage));
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
        } catch (error) {
            handleApiError(error);
        }
    };

    const onCloseModal = () => {
        setModal({ open: false, type: VIEW_TYPE.CREATE });
        setSelectedMenu(null);
    };

    const onImported = draft => {
        setSelectedMenu({
            ...draft,
            currencyCode: draft.currencyCode || DEFAULT_CURRENCY_BY_LOCALE[account?.locale] || 'USD',
        });
        setIsImportOpen(false);
        setModal({ open: true, type: VIEW_TYPE.CREATE });
    };

    return (
        <PageContainer>
            <PageHeader
                styles={{ marginBottom: '16px' }}
                icon={<MenusIcon />}
                title={t('MenusPage.title')}
                subtitle={t('MenusPage.subtitle')}
            >
                {canManage && (
                    <>
                        <Button
                            variant="contained"
                            onClick={() => setModal({ open: true, type: VIEW_TYPE.CREATE })}
                            id="pl-button-create-menu"
                        >
                            {t('MenusPage.createMenu')}
                        </Button>
                        <AiButton onClick={() => setIsImportOpen(true)} id="pl-button-import-menu">
                            {t('MenusPage.importWithAi')}
                        </AiButton>
                    </>
                )}
            </PageHeader>
            <MenusTable
                onClickMenu={onClickMenu}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
            />
            {isImportOpen ? (
                <ImportModal isOpen onClose={() => setIsImportOpen(false)} onImported={onImported} />
            ) : null}
            {modal.open ? (
                <EditModal
                    modal={modal}
                    onClose={onCloseModal}
                    selectedMenu={selectedMenu}
                    account={account}
                    page={page}
                    setPage={setPage}
                    rowsPerPage={rowsPerPage}
                />
            ) : null}
            {menuToDelete ? (
                <ModalConfirm
                    isOpen
                    handleClose={() => setMenuToDelete(null)}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDelete}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            ) : null}
        </PageContainer>
    );
};

export default MenusPage;
